import { usePopoverContext } from "@ark-ui/react";
import { ChevronDown } from "lucide-react";

import Popover from "@/components/popover/popover";

import styles from "./search-filter-pill.module.css";

export type SearchFilterOption = {
  value: string;
  label: string;
};

export type SearchFilterPillProps = {
  /** Pill label when nothing is selected — the type of data this filter scopes. */
  typeLabel: string;
  /** Menu row that clears the filter (not shown on the pill). */
  clearLabel: string;
  options: readonly SearchFilterOption[];
  value?: string;
  onChange: (value: string | undefined) => void;
};

/**
 * Single-select filter pill for a search bar. Unset shows {@link typeLabel};
 * a chosen option replaces that label. The menu's first row ({@link clearLabel})
 * clears the selection. Later filter types (e.g. unit) can reuse this control
 * with a different type label and options.
 */
export function SearchFilterPill({
  typeLabel,
  clearLabel,
  options,
  value,
  onChange,
}: SearchFilterPillProps) {
  const selected = options.find((option) => option.value === value);
  const displayLabel = selected?.label ?? typeLabel;

  return (
    <Popover
      nested
      rootProps={{ positioning: { placement: "bottom-start", gutter: 4 } }}
      trigger={(ctx) => (
        <button
          type="button"
          className={styles.trigger}
          data-open={ctx.open ? "" : undefined}
          data-selected={selected ? "" : undefined}
          aria-label={typeLabel}
        >
          <span className={styles.label}>{displayLabel}</span>
          <ChevronDown size={14} aria-hidden className={styles.chevron} />
        </button>
      )}
    >
      <SearchFilterPillMenu
        typeLabel={typeLabel}
        clearLabel={clearLabel}
        options={options}
        value={value}
        onChange={onChange}
      />
    </Popover>
  );
}

function SearchFilterPillMenu({
  typeLabel,
  clearLabel,
  options,
  value,
  onChange,
}: SearchFilterPillProps) {
  const popover = usePopoverContext();

  const choose = (next: string | undefined) => {
    onChange(next);
    popover.setOpen(false);
  };

  return (
    <div className={styles.menu} role="listbox" aria-label={typeLabel}>
      <button
        type="button"
        role="option"
        className={styles.option}
        aria-selected={!value}
        data-selected={!value ? "" : undefined}
        onClick={() => choose(undefined)}
      >
        {clearLabel}
      </button>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="option"
          className={styles.option}
          aria-selected={value === option.value}
          data-selected={value === option.value ? "" : undefined}
          onClick={() => choose(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
